import * as React from "react";
import {
  FileChartLine,
  GitBranch,
  GitFork,
  GitMerge,
  Layers,
  Play,
  RotateCcw,
  Shuffle,
  Wallet,
  type LucideIcon,
} from "lucide-react";

import { cn } from "@/lib/utils";
import type { WorkspaceNodeType } from "../../../../types/workspace";

const TYPE_ICONS: Record<WorkspaceNodeType, LucideIcon> = {
  account: Wallet,
  strategy: Layers,
  report: FileChartLine,
  simulation: Play,
  policy: GitBranch,
  combined_policy: GitMerge,
  combined_simulation: Shuffle,
};

const TYPE_RING_CLASS: Record<WorkspaceNodeType, string> = {
  account: "border-primary/50 bg-primary/10 text-primary",
  strategy: "border-violet-500/40 bg-violet-500/10 text-violet-600 dark:text-violet-300",
  report: "border-sky-500/40 bg-sky-500/10 text-sky-600 dark:text-sky-300",
  simulation: "border-[color:var(--tessera-pnl-positive)]/40 bg-[color:var(--tessera-pnl-positive)]/10 text-[color:var(--tessera-pnl-positive)]",
  policy: "border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-300",
  combined_policy: "border-lime-500/40 bg-lime-500/10 text-lime-700 dark:text-lime-300",
  combined_simulation: "border-teal-500/40 bg-teal-500/10 text-teal-700 dark:text-teal-300",
};

/**
 * Card outer stroke colors — hard RGB so theme / layered utilities cannot
 * mute them back to --border. Matches marketing hero type strokes.
 */
export const WORKSPACE_NODE_TYPE_STROKE_COLOR: Record<WorkspaceNodeType, string> = {
  account: "var(--brand-tropical-surf)",
  strategy: "rgb(139 92 246)",
  report: "rgb(14 165 233)",
  simulation: "var(--tessera-pnl-positive)",
  policy: "rgb(245 158 11)",
  combined_policy: "rgb(132 204 22)",
  combined_simulation: "rgb(20 184 166)",
};

/** Class hooks kept for selection/highlight composition; color is inline. */
export const WORKSPACE_NODE_TYPE_STROKE_CLASS: Record<WorkspaceNodeType, string> = {
  account: "border-solid",
  strategy: "border-solid",
  report: "border-solid",
  simulation: "border-solid",
  policy: "border-solid",
  combined_policy: "border-solid",
  combined_simulation: "border-solid",
};

/** Highlight ring when a combined_policy is selected and this policy is one of its sources. */
export const WORKSPACE_POLICY_COMBINED_SOURCE_HIGHLIGHT_CLASS =
  "ring-2 ring-amber-500/55 ring-offset-1 ring-offset-background border-amber-500 dark:ring-amber-400/50";

export const WORKSPACE_POLICY_COMBINED_SOURCE_STROKE = "rgb(245 158 11)";

/** Edit & run fork card strokes — match FORK_RING_CLASS (orange / emerald). */
const FORK_STROKE_COLOR: Partial<Record<WorkspaceNodeType, string>> = {
  policy: "rgb(249 115 22)", // orange-500
  combined_policy: "rgb(16 185 129)", // emerald-500
};

/** Simulation rerun accent — dark green (distinct from original sim pnl-positive). */
export const WORKSPACE_SIMULATION_RERUN_STROKE = "rgb(21 128 61)"; // green-700
export const WORKSPACE_COMBINED_SIMULATION_RERUN_STROKE = "rgb(4 120 87)"; // emerald-700

const RERUN_STROKE_COLOR: Partial<Record<WorkspaceNodeType, string>> = {
  simulation: WORKSPACE_SIMULATION_RERUN_STROKE,
  combined_simulation: WORKSPACE_COMBINED_SIMULATION_RERUN_STROKE,
};

/** Rerun sims: RotateCcw icon + dark green ring/stroke. */
const RERUN_RING_CLASS: Partial<Record<WorkspaceNodeType, string>> = {
  simulation:
    "border-green-700/45 bg-green-700/10 text-green-800 dark:border-green-500/45 dark:bg-green-500/10 dark:text-green-400",
  combined_simulation:
    "border-emerald-700/45 bg-emerald-700/10 text-emerald-800 dark:border-emerald-500/45 dark:bg-emerald-500/10 dark:text-emerald-400",
};

/** Edit & run forks: GitFork icon; warmer ring so they read apart from source policies. */
const FORK_RING_CLASS: Partial<Record<WorkspaceNodeType, string>> = {
  policy: "border-orange-500/45 bg-orange-500/10 text-orange-700 dark:text-orange-300",
  combined_policy: "border-emerald-500/45 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
};

const FALLBACK_ICON = Layers;
const FALLBACK_RING_CLASS = "border-border bg-muted/40 text-muted-foreground";
const FALLBACK_STROKE_CLASS = "border-solid";
const FALLBACK_STROKE_COLOR = "var(--border)";

export function workspaceNodeTypeStrokeClass(type: WorkspaceNodeType): string {
  return WORKSPACE_NODE_TYPE_STROKE_CLASS[type] ?? FALLBACK_STROKE_CLASS;
}

/** Inline border wins over globals that force --border on chrome. */
export function workspaceNodeTypeStrokeStyle(
  type: WorkspaceNodeType,
  options?: { isFork?: boolean; isRerun?: boolean }
): React.CSSProperties {
  const rerunColor =
    options?.isRerun && (type === "simulation" || type === "combined_simulation")
      ? RERUN_STROKE_COLOR[type]
      : undefined;
  const forkColor =
    options?.isFork && (type === "policy" || type === "combined_policy")
      ? FORK_STROKE_COLOR[type]
      : undefined;
  return {
    borderWidth: 1.5,
    borderStyle: "solid",
    borderColor:
      rerunColor ??
      forkColor ??
      WORKSPACE_NODE_TYPE_STROKE_COLOR[type] ??
      FALLBACK_STROKE_COLOR,
  };
}

export type WorkspaceNodeIconProps = {
  type: WorkspaceNodeType;
  variant?: "circle" | "card";
  /** Simulation date-window rerun stacked under a parent simulation. */
  isRerun?: boolean;
  /** Policy / combined_policy Edit & run fork under a source policy. */
  isFork?: boolean;
  className?: string;
  iconClassName?: string;
};

export function WorkspaceNodeIcon({
  type,
  variant = "circle",
  isRerun = false,
  isFork = false,
  className,
  iconClassName,
}: WorkspaceNodeIconProps): React.ReactElement {
  const isSimFamilyRerun =
    isRerun && (type === "simulation" || type === "combined_simulation");
  const isPolicyFamilyFork =
    isFork && (type === "policy" || type === "combined_policy");
  const Icon = isSimFamilyRerun
    ? RotateCcw
    : isPolicyFamilyFork
      ? GitFork
      : TYPE_ICONS[type] ?? FALLBACK_ICON;
  const ringClass = isSimFamilyRerun
    ? RERUN_RING_CLASS[type] ?? TYPE_RING_CLASS[type] ?? FALLBACK_RING_CLASS
    : isPolicyFamilyFork
      ? FORK_RING_CLASS[type] ?? TYPE_RING_CLASS[type] ?? FALLBACK_RING_CLASS
      : TYPE_RING_CLASS[type] ?? FALLBACK_RING_CLASS;

  if (variant === "card") {
    return (
      <div
        data-workspace-node-icon=""
        data-workspace-node-icon-variant="card"
        data-workspace-node-icon-rerun={isRerun ? "true" : undefined}
        data-workspace-node-icon-fork={isFork ? "true" : undefined}
        className={cn(
          "flex size-8 shrink-0 items-center justify-center rounded-md border",
          ringClass,
          className
        )}
      >
        <Icon className={cn("size-4", iconClassName)} strokeWidth={1.75} aria-hidden />
      </div>
    );
  }

  return (
    <div
      data-workspace-node-icon=""
      data-workspace-node-icon-rerun={isRerun ? "true" : undefined}
      data-workspace-node-icon-fork={isFork ? "true" : undefined}
      className={cn(
        "flex size-14 shrink-0 items-center justify-center rounded-full border-2 shadow-sm",
        ringClass,
        className
      )}
    >
      <Icon className={cn("size-6", iconClassName)} strokeWidth={1.75} aria-hidden />
    </div>
  );
}
